import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { combineLatest, filter, map, Observable, Subject, take, tap } from 'rxjs';
import { VsCodeEditorService } from './vscode-editor.service';

@UntilDestroy()
@Component({
  selector: 'y42-vscode-editor',
  templateUrl: './vscode-editor.component.html',
  styleUrls: ['./vscode-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VsCodeEditorService],
})
export class VsCodeEditorComponent implements AfterViewInit {
  /**
   * HACK:
   * as-split dragging does not work with iframes,
   * the drag event stops being emitted the second the mouse hovers over the iframe.
   *
   * To mitigate this we can set "pointer-events: none" on the iframe while the drag is happening.
   *
   * https://github.com/angular-split/angular-split/issues/31
   */
  @Input()
  @InputObservable()
  parentIsInDraggingState: boolean | null = false;
  private readonly parentIsInDraggingState$!: Observable<boolean | null>;

  @Input() pathToOpen = '';

  @Input() propertyPathToOpen: string | undefined = '';

  @Input() completionConfig: Partial<typeof monaco.languages.sql.completionConfig> = {};

  @Input() openMergeEditor = false;

  @Output() activeFileChanged = this.codeEditorService.activeFile$;
  @Output() activeFileSaved = this.codeEditorService.activeFileSaved$;
  @Output() ctrlKClicked = this.codeEditorService.ctrlKClicked$;

  @Output() activeYmlPropertyPathChanged = new EventEmitter<string | null | undefined>();

  @Output() completeMerge = new EventEmitter<string>();

  constructor(private readonly sanitizer: DomSanitizer, private readonly codeEditorService: VsCodeEditorService) {}

  @ViewChild('iframe') set iframeRef(iframeRef: ElementRef<HTMLIFrameElement>) {
    this.iframeRef$.next(iframeRef);
  }
  protected readonly iframeLoaded$ = new Subject<Event>();
  private readonly iframeRef$ = new Subject<ElementRef<HTMLIFrameElement>>();

  readonly state = connectState(this, {
    isLoading: this.codeEditorService.loading$,
    vsCodeUrl: this.codeEditorService.url$.pipe(map((url) => this.sanitizer.bypassSecurityTrustResourceUrl(url))),
    iframePointerEvents: this.parentIsInDraggingState$.pipe(map((dragging) => (dragging ? `none` : undefined))),
  });

  ngAfterViewInit() {
    this.initializeCodeEditorOnIFrameLoad().pipe(untilDestroyed(this)).subscribe();
    this.listenToActiveYmlPropertyPathChange().pipe(untilDestroyed(this)).subscribe();
    this.completePathMerge().pipe(untilDestroyed(this)).subscribe();
  }

  openPath(path: string) {
    this.codeEditorService.openPath(path);
  }

  private listenToActiveYmlPropertyPathChange() {
    return this.codeEditorService.activeYmlPropertyPath$.pipe(
      tap((path) => this.activeYmlPropertyPathChanged.emit(path)),
    );
  }

  private completePathMerge() {
    return this.codeEditorService.completeMerge$.pipe(tap((path) => this.completeMerge.emit(path)));
  }

  private initializeCodeEditorOnIFrameLoad() {
    return combineLatest([this.iframeRef$, this.iframeLoaded$]).pipe(
      map(([ref]) => ref?.nativeElement),
      filter(Boolean),
      tap((iframe) =>
        this.codeEditorService.initialize({
          iframe,
          pathToOpen: this.pathToOpen,
          propertyPathToOpen: this.propertyPathToOpen || '',
          openMergeEditor: this.openMergeEditor,
          completionConfig: this.completionConfig,
        }),
      ),
      take(1),
    );
  }
}
